import { type Dispatch, type SetStateAction, useCallback, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import { useSearchParameters } from '@/components/signup/hooks/use-search-parameters';
import { isJsmPlaygroundSignup } from '@/components/signup/utils/jsm-playground-signup-intent';
import { experiments } from '@/utils/experiment/manifest';
import { fireTrackingEvents, getExperimentWithExposures } from '@/utils/experiment/use-experiment';
import { useLoggedInUser } from '@/utils/logged-in-user-provider';

import {
	INCIDENT_TEMPLATE_ID,
	type JsmIncidentManagementIntent,
} from '../views/jsm-use-case-provisioning/jsm-incident-management-setup-state';

import type { JsmUseCaseTemplateOption } from '../views/jsm-use-case-template-choice/jsm-use-case-template-choice-constants';

export const useJsmIncidentManagementIntent = (
	setTemplateId: Dispatch<SetStateAction<string | undefined>>,
) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const { accountId, isUserLoggedIn, requestInProgress } = useLoggedInUser();
	const { signupId, onDemandUrl } = useSearchParameters();
	const [intent, setIntent] = useState<JsmIncidentManagementIntent>();
	const setSelectedTemplateId = useCallback<Dispatch<SetStateAction<string | undefined>>>(
		(value) => {
			setIntent(undefined);
			setTemplateId(value);
		},
		[setTemplateId],
	);

	const selectTemplateOption = useCallback(
		(option?: JsmUseCaseTemplateOption) => {
			setSelectedTemplateId(option?.templateId);
			if (!isUserLoggedIn || !accountId || requestInProgress) return;
			if (
				option?.cardKey !== 'incident-management' ||
				option.templateId !== INCIDENT_TEMPLATE_ID ||
				isJsmPlaygroundSignup(window.location.search)
			)
				return;
			if (window.featureGateBootstrapValues?.identifiers?.atlassianAccountId !== accountId) return;
			try {
				const experiment = getExperimentWithExposures(experiments.operationsLandFlow);
				if (experiment.type !== 'enrolled') return;
				fireTrackingEvents(
					experiments.operationsLandFlow.experimentName,
					analyticsClient,
					createAnalyticsEvent,
					{ usingDefault: experiment.usingDefault },
					experiment.value,
				);
				if (experiment.value !== true) return;
			} catch {
				return;
			}
			setIntent({
				source: 'selector',
				cardKey: 'incident-management',
				accountId,
				signupId,
				siteName: onDemandUrl,
			});
		},
		[
			accountId,
			isUserLoggedIn,
			requestInProgress,
			signupId,
			onDemandUrl,
			setSelectedTemplateId,
			analyticsClient,
			createAnalyticsEvent,
		],
	);

	return { incidentManagementIntent: intent, selectTemplateOption, setSelectedTemplateId };
};
